import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { createIntl, createIntlCache, FormattedMessage } from 'react-intl';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import messages from '../../assets/messages';
import { setActiveNetwork } from '../../redux/slices/filters';
import {
  updateListVariant,
  updateShowFilters
} from '../../redux/slices/ui/layout';
import ReducerConstants from '../../_shared/constants/reducers';
import renderLocale from '../../_shared/helpers/renderLocale';
import {
  ListVariant,
  NetworkTypes,
  TradeContext,
  TradeState,
  TradeStateName
} from '../../_shared/types/trade';
import {
  tradeSortOptionsActive,
  tradeSortOptionsUpcoming
} from '../../_shared/hooks/trade/sort/constants/tradeSortMethods';
import { objectToQueryString } from '../../_shared/utils/url';
import MixpanelConfig from '../../_shared/constants/mixpanelConfig';
import SegmentConfig from '../../_shared/constants/segmentConfig';
import { localStorageService } from '../../services/StorageService';
import { CacheKeys } from '../../_shared/constants/filters';
import useFeatureFlags from '../../_shared/hooks/useFeatureFlags';
import FEATURE_FLAGS from '../../_shared/constants/featureFlags';
import { ListVariantSource } from '../../_shared/types/auction';
import UserPreferencesService from '../../services/UserPreferencesService';
import Icon from '../../_shared/components/Icon';
import Select from '../../_shared/components/Select';
import useAnalytics from '../../_shared/hooks/useAnalytics';
import {
  FiltersIcon,
  StyledDealerGroupFilter,
  StyledDropdownContainer,
  StyledFiltersButton,
  StyledGridIcon,
  StyledIconContainer,
  StyledListIcon,
  StyledListVariantToggle,
  StyledOptionsFilter,
  StyledSelect,
  StyledTradeFilterBar
} from './Styles';

const propTypes = {
  showLayoutToggle: PropTypes.bool,
  showSortOptions: PropTypes.bool,
  setHasNetwork: PropTypes.func,
  isOmp: PropTypes.bool
};

const defaultProps = {
  showLayoutToggle: true,
  showSortOptions: true,
  setHasNetwork: () => {},
  isOmp: false
};
const addQueryParam = (key, value) => {
  const url = new URL(window.location.href);
  url.searchParams.set(key, value);
  window.history.pushState({}, '', url.toString());
};
const TradeFilterBar = ({
  selectedSortOption,
  setSortOption,
  tradeState,
  filtersButtonRef,
  showLayoutToggle,
  showSortOptions,
  setHasNetwork,
  isOmp
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = window.location;
  const locale = renderLocale();
  const intl = createIntl(
    { locale, messages: messages[locale] },
    createIntlCache()
  );
  const track = useAnalytics();
  const userPreferenceService = UserPreferencesService.get();
  const isLayoutSelectable =
    useFeatureFlags(FEATURE_FLAGS.IS_GRID_VIEW_ENABLED) && showLayoutToggle;

  const [
    tradeSection,
    listVariant,
    showFilters,
    auctionSource,
    activeNetwork
  ] = useSelector(
    state => [
      state?.ui?.nav?.primary?.context,
      state?.ui?.layout?.listVariant,
      state?.ui?.layout?.showFilters,
      state[ReducerConstants.AUTH]?.user?.auctionSource,
      state?.filter?.activeNetwork?.[tradeState]
    ],
    shallowEqual
  );

  const sortOptions = localStorageService.getItem(CacheKeys.sortOptions) || {
    [TradeState.active]: tradeSortOptionsActive,
    [TradeState.upcoming]: tradeSortOptionsUpcoming
  };

  const tradeContextNetworks = {
    [TradeContext.Buy]: 'buyingNetworks',
    [TradeContext.Sell]: 'sellingNetworks'
  };

  // Find all networks based on buy/sell context and stash them in an array for the dropdown
  const dealerGroupFilters =
    auctionSource &&
    auctionSource[tradeContextNetworks[tradeSection]]?.reduce(
      (acc, network) => {
        if (network.isDealerGroupNetwork || network.isOpenNetwork) {
          acc.push({
            id: network.id,
            value: network.id,
            label: network.displayName,
            title: network.displayName,
            type: NetworkTypes.buyerNetwork.type,
            isOpenNetwork: network.isOpenNetwork
          });
        }
        return acc;
      },
      [
        {
          id: 'all',
          value: 'all',
          label: intl.formatMessage({ id: 'list.filters.networks.all' }),
          icon: 'TR5-Network'
        }
      ]
    );

  useEffect(() => {
    // In the pending case we might not need to show the filter
    // if there's nothing other than "All Networks" and "Open Network"
    setHasNetwork(dealerGroupFilters?.length > 2);
  }, [setHasNetwork, dealerGroupFilters]);

  const trackNetworkChange = activeDGN => {
    let networkType = SegmentConfig.networkType.internal;
    if (!activeDGN || activeDGN?.id === 'all') {
      networkType = SegmentConfig.networkType.all;
    }

    if (activeDGN?.isOpenNetwork) {
      networkType = SegmentConfig.networkType.open;
    }

    track(SegmentConfig.names.networkDropdown, {
      network_type: networkType
    });
  };

  // Dispatch the filter change option to redux filter active network
  const handleFilterChange = id => {
    const activeDGN =
      dealerGroupFilters.find(option => Number(option.id) === id) ||
      dealerGroupFilters.find(option => option.id === 'all');

    const networkCacheLocal =
      localStorageService.getItem(CacheKeys.selectedNetworkOption) ?? {};
    networkCacheLocal[tradeState] = activeDGN?.id;

    localStorageService.setItem(
      CacheKeys.selectedNetworkOption,
      networkCacheLocal
    );
    dispatch(setActiveNetwork(networkCacheLocal));

    const urlParams = new URLSearchParams(search);

    if (isOmp) {
      addQueryParam('networkId', activeDGN?.id);
      trackNetworkChange(activeDGN);
    } else {
      history.push({
        pathname: window.location.pathname,
        search: objectToQueryString({
          ...Object.fromEntries(urlParams),
          networkId: activeDGN?.id
        })
      });
      track(MixpanelConfig.names.dgnFilter, {
        list_type: ListVariantSource[listVariant],
        network_option: activeDGN ? activeDGN?.label : 'All networks',
        trade_state: TradeStateName[tradeState]
      });
    }
  };

  const toggleFilters = () => dispatch(updateShowFilters(!showFilters));

  const onListVariantToggleClick = selectedVariant => {
    if (selectedVariant !== listVariant) {
      dispatch(updateListVariant(selectedVariant));
      userPreferenceService.updateListVariant(selectedVariant);
      track(MixpanelConfig.names.gridViewToggle, {
        grid_view_on: selectedVariant === ListVariant.GRID
      });
    }
  };

  return (
    <StyledTradeFilterBar
      data-testid="tradesFilterBar"
      hasDGN={dealerGroupFilters?.length > 1}
    >
      {dealerGroupFilters?.length > 2 && (
        <StyledDealerGroupFilter>
          <StyledDropdownContainer>
            <StyledIconContainer>
              <Icon icon="TR5-Network" />
            </StyledIconContainer>
            <StyledSelect
              testId="dealerGroupSort"
              className="dealerGroupSort"
              options={dealerGroupFilters}
              onChange={handleFilterChange}
              defaultValue={activeNetwork}
            />
          </StyledDropdownContainer>
        </StyledDealerGroupFilter>
      )}
      <StyledOptionsFilter>
        {listVariant === ListVariant.GRID && (
          <StyledFiltersButton ref={filtersButtonRef} onClick={toggleFilters}>
            <FormattedMessage id="list.filters.headertext" /> <FiltersIcon />
          </StyledFiltersButton>
        )}
        {isLayoutSelectable && (
          <StyledListVariantToggle>
            {listVariant === ListVariant.GRID ? (
              <FormattedMessage id="layout.grid" />
            ) : (
              <FormattedMessage id="layout.list" />
            )}
            <StyledListIcon
              isSelected={listVariant === ListVariant.LIST}
              clickHandler={() => onListVariantToggleClick(ListVariant.LIST)}
            />
            <StyledGridIcon
              isSelected={listVariant === ListVariant.GRID}
              clickHandler={() => onListVariantToggleClick(ListVariant.GRID)}
            />
          </StyledListVariantToggle>
        )}
        {showSortOptions && sortOptions && (
          <Select
            testId="tradesSort"
            onChange={setSortOption}
            defaultValue={selectedSortOption}
            options={sortOptions[tradeState]}
          >
            {/* Sorted */}
            <FormattedMessage id="list.sorting.label" />
          </Select>
        )}
      </StyledOptionsFilter>
    </StyledTradeFilterBar>
  );
};

TradeFilterBar.prototype = propTypes;
TradeFilterBar.defaultProps = defaultProps;

export default TradeFilterBar;
